import { Controller } from 'stimulus';

export default class extends Controller {
  static targets = [
    'lane', 'laneBody', 'card', 'empty',
    'palette', 'paletteBuiltins',
    'tray', 'trayBody', 'trayChip',
    'laneInput', 'formTypeInput', 'requiredInput',
  ];

  connect() {
    this.documentClickHandler = this.handleDocumentClick.bind(this);
    document.addEventListener('click', this.documentClickHandler);

    this.enableSortable();
    this.enableTrayDrag();
    this.refreshEmptyStates();
  }

  disconnect() {
    document.removeEventListener('click', this.documentClickHandler);
  }

  // --- jQuery-UI sortable (already a project dep via sortable_controller) --
  enableSortable() {
    if (typeof $ === 'undefined' || !$.fn || !$.fn.sortable) return;

    const bodies = this.laneBodyTargets;
    if (bodies.length === 0) return;

    bodies.forEach((body) => {
      $(body).sortable({
        items: '> .template-builder__card',
        handle: '.template-builder__card-grip',
        connectWith: bodies.filter((b) => b !== body),
        tolerance: 'pointer',
        placeholder: 'template-builder__card template-builder__card--placeholder',
        forcePlaceholderSize: true,
        receive: (event, ui) => this.onReceive(ui.item[0]),
        stop: () => {
          this.pinAddButtonsToBottom();
          this.refreshEmptyStates();
        },
      });
    });
  }

  // Tray offchips → draggable, lane bodies → droppable. Drop an offchip
  // onto a lane and we materialize the card there (same path as Add).
  enableTrayDrag() {
    if (typeof $ === 'undefined' || !$.fn || !$.fn.draggable || !$.fn.droppable) return;

    this.trayChipTargets.forEach((chip) => this.makeChipDraggable(chip));

    this.laneBodyTargets.forEach((body) => {
      $(body).droppable({
        accept: '.template-builder__offchip',
        hoverClass: 'is-drop-target',
        tolerance: 'pointer',
        drop: (event, ui) => {
          const name = ui.draggable[0].dataset.builtin;
          const lane = body.parentElement.dataset.lane;
          this.materializeBuiltinCard(name, lane);
        },
      });
    });
  }

  makeChipDraggable(chip) {
    if (typeof $ === 'undefined' || !$.fn || !$.fn.draggable) return;
    $(chip).draggable({
      helper: 'clone',
      revert: 'invalid',
      revertDuration: 150,
      cursor: 'move',
      appendTo: 'body',
      zIndex: 9999,
    });
  }

  // jQuery-UI sortable can leave non-sortable siblings (the empty-state and
  // the Add button) above the dropped card. After every sort, force them
  // back to the bottom of their lane.
  pinAddButtonsToBottom() {
    this.laneBodyTargets.forEach((body) => {
      const empty = body.querySelector('[data-target="template-builder.empty"]');
      const add = body.querySelector('.template-builder__add');
      if (empty) body.appendChild(empty);
      if (add) body.appendChild(add);
    });
  }

  // Called when a card is dropped into a lane different from its origin.
  onReceive(cardEl) {
    const newLane = cardEl.closest('[data-lane]').dataset.lane;
    this.syncCardLane(cardEl, newLane);
  }

  // --- Palette ----------------------------------------------------------------
  openPalette(event) {
    event.preventDefault();
    event.stopPropagation();
    const palette = event.currentTarget.parentElement.querySelector('.template-builder__palette');
    if (!palette) return;
    const wasHidden = palette.classList.contains('is-hidden');
    this.closeAllPalettes();
    this.closeAllMenus();
    if (wasHidden) palette.classList.remove('is-hidden');
  }

  closeAllPalettes() {
    this.paletteTargets.forEach((p) => p.classList.add('is-hidden'));
  }

  // Palette option click: turn on a built-in and drop it into this palette's
  // lane.
  addBuiltin(event) {
    event.preventDefault();
    event.stopPropagation();
    const opt = event.currentTarget;
    const name = opt.dataset.builtin;
    const lane = opt.closest('[data-lane]').dataset.lane;
    this.materializeBuiltinCard(name, lane);
    this.closeAllPalettes();
  }

  // Clone the pre-rendered <template id="tb-tmpl-<name>"> card into the
  // given lane, then clean up the chip and palette entries. Shared by the
  // Add-button path and the tray-to-lane drag path.
  materializeBuiltinCard(name, lane) {
    if (!name || !lane) return;
    const laneBody = this.laneBodyTargets.find(
      (b) => b.parentElement.dataset.lane === lane,
    );
    if (!laneBody) return;

    const tmpl = document.getElementById('tb-tmpl-' + name);
    if (!tmpl) return;
    const card = tmpl.content.firstElementChild.cloneNode(true);
    this.syncCardLane(card, lane);

    const addBlock = laneBody.querySelector('.template-builder__add');
    laneBody.insertBefore(card, addBlock);

    this.removeFromTray(name);
    this.removeFromPalettes(name);
    this.pinAddButtonsToBottom();
    this.refreshEmptyStates();
  }

  // --- Kebab menu -------------------------------------------------------------
  toggleMenu(event) {
    event.preventDefault();
    event.stopPropagation();
    const card = event.currentTarget.closest('.template-builder__card');
    const existing = card.querySelector('.template-builder__menu');
    this.closeAllMenus();
    if (existing) return;

    const lane = card.closest('[data-lane]').dataset.lane;
    const otherLane = lane === 'request' ? 'lab' : 'request';
    const otherLabel = otherLane === 'request' ? 'The Request' : 'For Lab Use';
    const moveArrow = otherLane === 'request' ? '←' : '→';
    const kind = card.dataset.kind;

    const menu = document.createElement('div');
    menu.className = 'template-builder__menu';
    const moveItem = `<div class="template-builder__menu-opt" data-action="click->template-builder#moveCard" data-target-lane="${otherLane}">${moveArrow} Move to ${otherLabel}</div>`;
    const offItem = kind === 'builtin'
      ? `<div class="template-builder__menu-sep"></div><div class="template-builder__menu-opt is-danger" data-action="click->template-builder#turnOff">⊘ Turn off (to tray)</div>`
      : '';
    menu.innerHTML = moveItem + offItem;
    card.appendChild(menu);
  }

  closeAllMenus() {
    document
      .querySelectorAll('.template-builder__menu')
      .forEach((m) => m.remove());
  }

  // --- Card actions -----------------------------------------------------------
  moveCard(event) {
    event.preventDefault();
    event.stopPropagation();
    const opt = event.currentTarget;
    const card = opt.closest('.template-builder__card');
    const targetLane = opt.dataset.targetLane;
    const targetBody = this.laneBodyTargets.find(
      (b) => b.parentElement.dataset.lane === targetLane,
    );
    if (!targetBody) return;

    const addBlock = targetBody.querySelector('.template-builder__add');
    targetBody.insertBefore(card, addBlock);
    this.syncCardLane(card, targetLane);
    this.closeAllMenus();
    this.pinAddButtonsToBottom();
    this.refreshEmptyStates();
  }

  // Flip the red asterisk between required (solid red) and optional (faint).
  toggleRequired(event) {
    event.preventDefault();
    event.stopPropagation();
    const star = event.currentTarget;
    const card = star.closest('.template-builder__card');
    if (!card) return;

    const isOn = !star.classList.contains('is-off');
    const nextOn = !isOn;

    star.classList.toggle('is-off', !nextOn);
    star.setAttribute('aria-pressed', String(nextOn));
    star.setAttribute(
      'title',
      nextOn ? 'Required' : 'Optional, click to require',
    );

    const input = card.querySelector('input[data-target="template-builder.requiredInput"]');
    if (input) input.value = nextOn ? '1' : '0';
  }

  turnOff(event) {
    event.preventDefault();
    event.stopPropagation();
    const card = event.currentTarget.closest('.template-builder__card');
    if (card.dataset.kind !== 'builtin') return;

    const name = card.dataset.builtin;
    const label = card.querySelector('.template-builder__card-name').textContent.trim();
    const type = card.querySelector('.template-builder__card-type')?.textContent.trim() || '';
    this.addToTray(name, label, type);
    this.addToPalettes(name, label, type);
    card.remove();
    this.closeAllMenus();
    this.refreshEmptyStates();
  }

  // --- Sync helpers -----------------------------------------------------------
  syncCardLane(card, newLane) {
    const kind = card.dataset.kind;
    if (kind === 'builtin') {
      const input = card.querySelector('input[data-target="template-builder.laneInput"]');
      if (input) input.value = newLane;
    } else if (kind === 'custom') {
      const input = card.querySelector('input[data-target="template-builder.formTypeInput"]');
      if (input) input.value = newLane === 'lab' ? 'additional' : 'initial';
    }
  }

  refreshEmptyStates() {
    this.laneBodyTargets.forEach((body) => {
      const empty = body.querySelector('[data-target="template-builder.empty"]');
      if (!empty) return;
      const hasCards = body.querySelector('.template-builder__card') !== null;
      empty.style.display = hasCards ? 'none' : '';
    });
  }

  // --- Tray <-> palettes ------------------------------------------------------
  removeFromTray(name) {
    this.trayChipTargets
      .filter((chip) => chip.dataset.builtin === name)
      .forEach((chip) => chip.remove());
  }

  addToTray(name, label, type) {
    if (!this.hasTrayBodyTarget) return;
    if (this.trayChipTargets.some((c) => c.dataset.builtin === name)) return;
    const chip = document.createElement('span');
    chip.className = 'template-builder__offchip';
    chip.dataset.target = 'template-builder.trayChip';
    chip.dataset.builtin = name;
    chip.dataset.label = label;
    chip.dataset.type = type;
    chip.innerHTML = `<span class="grip">⠿</span>${this.escape(label)}`;
    this.trayBodyTarget.appendChild(chip);
    this.makeChipDraggable(chip);
  }

  removeFromPalettes(name) {
    document
      .querySelectorAll(`[data-target="template-builder.paletteBuiltins"] .template-builder__palette-opt[data-builtin="${name}"]`)
      .forEach((opt) => opt.remove());
  }

  addToPalettes(name, label, type) {
    document
      .querySelectorAll('[data-target="template-builder.paletteBuiltins"]')
      .forEach((list) => {
        const lane = list.dataset.lane;
        const opt = document.createElement('div');
        opt.className = 'template-builder__palette-opt';
        opt.dataset.action = 'click->template-builder#addBuiltin';
        opt.dataset.builtin = name;
        opt.dataset.label = label;
        opt.dataset.type = type;
        opt.innerHTML = `<span class="nm">${this.escape(label)}</span><span class="template-builder__card-type">${this.escape(type)}</span>`;
        list.appendChild(opt);
      });
  }

  // --- Doc click → close menus & palettes -------------------------------------
  handleDocumentClick(event) {
    if (event.target.closest('.template-builder__palette')) return;
    if (event.target.closest('.template-builder__add-btn')) return;
    if (event.target.closest('.template-builder__menu')) return;
    if (event.target.closest('.template-builder__card-kebab')) return;
    this.closeAllPalettes();
    this.closeAllMenus();
  }

  escape(str) {
    return String(str || '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
  }
}
